import React from 'react';
import {
  Card,
  Typography,
  Grid,
  Box,
  Stack,
  Chip,
} from '@mui/material';
import { Tag, ShieldAlert, Users } from 'lucide-react';

export const QueueDetailsSection = ({
  features = {},
  state = 'running',
  consumerCount = 0,
  bindingCount = 0,
}) => {
  const featureKeys = Object.keys(features || {});

  return (
    <Card sx={{ p: 2.5 }}>
      <Box sx={{ pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
          Details & Arguments
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Features & Arguments */}
        <Grid item xs={12} md={8}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              color: 'text.secondary',
              display: 'block',
              mb: 1.5,
              fontSize: '0.7rem',
            }}
          >
            Features & Arguments
          </Typography>

          {featureKeys.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
              No special arguments defined
            </Typography>
          ) : (
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              {featureKeys.map((key) => {
                const val = features[key];
                const isDlx = key.includes('dead-letter');
                return (
                  <Chip
                    key={key}
                    icon={
                      isDlx ? (
                        <ShieldAlert style={{ width: 14, height: 14, color: '#006951' }} />
                      ) : (
                        <Tag style={{ width: 14, height: 14, color: '#64748b' }} />
                      )
                    }
                    label={
                      <span>
                        <strong>{key}:</strong> {String(val)}
                      </span>
                    }
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      height: 'auto',
                      py: 0.6,
                      px: 0.5,
                      bgcolor: isDlx ? 'rgba(0, 105, 81, 0.1)' : '#f1f5f9',
                      border: '1px solid',
                      borderColor: isDlx ? 'rgba(0, 105, 81, 0.3)' : 'divider',
                      color: isDlx ? '#006951' : 'text.primary',
                    }}
                  />
                );
              })}
            </Stack>
          )}
        </Grid>

        {/* State, Consumers, Bindings */}
        <Grid item xs={12} md={4}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  display: 'block',
                  fontSize: '0.7rem',
                }}
              >
                State
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1} mt={1}>
                <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: '#10b981' }} />
                <Typography variant="body2" sx={{ fontWeight: 800, textTransform: 'capitalize' }}>
                  {state || 'running'}
                </Typography>
              </Stack>
            </Grid>

            <Grid item xs={4}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  display: 'block',
                  fontSize: '0.7rem',
                }}
              >
                Consumers
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.8} mt={1}>
                <Users style={{ width: 16, height: 16, color: '#0284c7' }} />
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {consumerCount}
                </Typography>
              </Stack>
            </Grid>

            <Grid item xs={4}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  display: 'block',
                  fontSize: '0.7rem',
                }}
              >
                Bindings
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.8} mt={1}>
                <Tag style={{ width: 16, height: 16, color: '#7c3aed' }} />
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {bindingCount}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
};
